import React, { useState, useRef } from "react";
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Typography,
  Tooltip, Divider, Avatar, Menu, MenuItem, Collapse
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import BuildIcon from "@mui/icons-material/Build";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LinkIcon from "@mui/icons-material/Link";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FunctionsIcon from "@mui/icons-material/Functions";
import PaymentIcon from "@mui/icons-material/Payment";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import BarChartIcon from "@mui/icons-material/BarChart";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HomeIcon from "@mui/icons-material/Home";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MessageIcon from "@mui/icons-material/Message";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import SendIcon from "@mui/icons-material/Send";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { changeLanguage, SUPPORTED_LANGUAGES, type AppLanguage } from "../i18n/index.js";
import { useVersionsStore } from "../store/versionsStore.js";
// Logo brand Kappa Equestre (favicon storico) in un badge bianco per leggibilità su sfondo scuro.
function KappaLogo({ size = 32 }: { size?: number }) {
  return (
    <Box sx={{
      width: size + 8, height: size + 8, p: 0.5, flexShrink: 0,
      borderRadius: 1.5, bgcolor: "#FFFFFF",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <img src="/logo-kappa.png" alt="Kappa Equestre" width={size} height={size} style={{ display: "block", objectFit: "contain" }} />
    </Box>
  );
}

export const DRAWER_WIDTH = 248;
export const MINI_DRAWER_WIDTH = 72;

const SIDEBAR_BG = "#0F1F14";
const SIDEBAR_HOVER = "rgba(82,183,136,0.12)";
const SIDEBAR_SELECTED = "rgba(82,183,136,0.22)";
const SIDEBAR_TEXT = "rgba(255,255,255,0.87)";
const SIDEBAR_SUBTEXT = "rgba(255,255,255,0.55)";
const SIDEBAR_DIVIDER = "rgba(255,255,255,0.10)";

interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

interface NavItem {
  labelKey: string;
  path: string;
  icon: React.ReactNode;
}

// I label vengono risolti via i18n (t) nei componenti; qui solo le chiavi.
const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: "",
    items: [
      { labelKey: "sidebar.dashboard", path: "/protected", icon: <HomeIcon fontSize="small" /> }
    ]
  },
  // Blocco 1 — tutte le operazioni sui soci. Sequenza allineata alle card della dashboard
  // (Soci, Ricevute, Statistiche, Riepilogo, Preparazione mensili, Quote Periodiche,
  // Corsi/extra, Storia pagamenti); Crediti non è tra le card → in fondo.
  {
    labelKey: "sidebar.groupMembers",
    items: [
      { labelKey: "nav.partners", path: "/protected/partners", icon: <PeopleIcon fontSize="small" /> },
      { labelKey: "nav.receipts", path: "/protected/receipts", icon: <ReceiptIcon fontSize="small" /> },
      { labelKey: "nav.statistics", path: "/protected/statistics", icon: <BarChartIcon fontSize="small" /> },
      { labelKey: "nav.summary", path: "/protected/summary", icon: <AccountBalanceIcon fontSize="small" /> },
      { labelKey: "nav.creditsGenerate", path: "/protected/credits-generate", icon: <FunctionsIcon fontSize="small" /> },
      { labelKey: "nav.partnerServices", path: "/protected/partner-services", icon: <LinkIcon fontSize="small" /> },
      { labelKey: "nav.services", path: "/protected/services", icon: <BuildIcon fontSize="small" /> },
      { labelKey: "nav.debits", path: "/protected/debits", icon: <PaymentIcon fontSize="small" /> },
      { labelKey: "nav.credits", path: "/protected/credits", icon: <TrendingUpIcon fontSize="small" /> }
    ]
  },
  // Blocco 2 — comunicare con i soci
  {
    labelKey: "sidebar.groupCommunications",
    items: [
      { labelKey: "sidebar.composeWa", path: "/protected/whatsapp/compose", icon: <SendIcon fontSize="small" /> },
      { labelKey: "sidebar.queuesWa", path: "/protected/whatsapp/queues", icon: <FormatListBulletedIcon fontSize="small" /> },
      { labelKey: "sidebar.messagesWa", path: "/protected/whatsapp/messages", icon: <MessageIcon fontSize="small" /> },
      { labelKey: "sidebar.templatesWa", path: "/protected/whatsapp/templates", icon: <WhatsAppIcon fontSize="small" /> }
    ]
  },
  // Blocco 3 — fornitori e costi
  {
    labelKey: "sidebar.groupSuppliers",
    items: [
      { labelKey: "nav.suppliers", path: "/protected/suppliers", icon: <BusinessIcon fontSize="small" /> },
      { labelKey: "nav.acquiredServices", path: "/protected/services-acquired", icon: <PlaylistAddCheckIcon fontSize="small" /> },
      { labelKey: "nav.costs", path: "/protected/costs", icon: <MoneyOffIcon fontSize="small" /> }
    ]
  }
];

const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

// Voce attiva se il path corrisponde esattamente o è una sotto-rotta (path + "/").
// Il confine di segmento evita falsi positivi tra fratelli con prefisso comune,
// es. /protected/credits-generate NON deve attivare /protected/credits.
const isNavSelected = (currentPath: string, path: string) =>
  currentPath === path || (path !== "/protected" && currentPath.startsWith(path + "/"));

function NavGroupBlock({
  group,
  currentPath,
  onNavigate
}: {
  group: NavGroup;
  currentPath: string;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const hasLabel = group.labelKey !== "";

  return (
    <>
      {hasLabel && (
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Box
            onClick={() => setOpen(!open)}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              cursor: "pointer", userSelect: "none"
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: SIDEBAR_SUBTEXT, fontWeight: 700,
                textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.65rem"
              }}
            >
              {t(group.labelKey)}
            </Typography>
            {open
              ? <ExpandLess sx={{ color: SIDEBAR_SUBTEXT, fontSize: 14 }} />
              : <ExpandMore sx={{ color: SIDEBAR_SUBTEXT, fontSize: 14 }} />}
          </Box>
          <Divider sx={{ borderColor: SIDEBAR_DIVIDER, mt: 0.5 }} />
        </Box>
      )}
      <Collapse in={open} timeout="auto">
        <List disablePadding sx={{ px: 1, pb: 0.5 }}>
          {group.items.map((item) => {
            const selected = isNavSelected(currentPath, item.path);
            return (
              <ListItemButton
                key={item.path}
                data-tour={`nav:${item.path}`}
                selected={selected}
                onClick={() => onNavigate(item.path)}
                sx={{
                  borderRadius: 1.5, mb: 0.25, py: 0.75,
                  color: selected ? "#52B788" : SIDEBAR_TEXT,
                  bgcolor: selected ? SIDEBAR_SELECTED : "transparent",
                  "&:hover": { bgcolor: SIDEBAR_HOVER },
                  "&.Mui-selected": { bgcolor: SIDEBAR_SELECTED },
                  "&.Mui-selected:hover": { bgcolor: SIDEBAR_SELECTED }
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: selected ? "#52B788" : SIDEBAR_SUBTEXT }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={t(item.labelKey)}
                  primaryTypographyProps={{
                    fontSize: "0.85rem",
                    fontWeight: selected ? 600 : 400
                  }}
                />
                {selected && (
                  <Box sx={{
                    width: 3, height: 20, bgcolor: "#52B788",
                    borderRadius: 4, ml: 1
                  }} />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Collapse>
    </>
  );
}

function MiniNavList({
  currentPath,
  onNavigate
}: {
  currentPath: string;
  onNavigate: (path: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <List disablePadding sx={{ px: 0.5, py: 0.5 }}>
      {ALL_NAV_ITEMS.map((item) => {
        const selected = isNavSelected(currentPath, item.path);
        return (
          <Tooltip key={item.path} title={t(item.labelKey)} placement="right">
            <ListItemButton
              data-tour={`nav:${item.path}`}
              selected={selected}
              onClick={() => onNavigate(item.path)}
              sx={{
                borderRadius: 1.5, mb: 0.25, py: 1, justifyContent: "center",
                color: selected ? "#52B788" : SIDEBAR_TEXT,
                bgcolor: selected ? SIDEBAR_SELECTED : "transparent",
                "&:hover": { bgcolor: SIDEBAR_HOVER },
                "&.Mui-selected": { bgcolor: SIDEBAR_SELECTED },
                "&.Mui-selected:hover": { bgcolor: SIDEBAR_SELECTED }
              }}
            >
              <ListItemIcon sx={{ minWidth: 0, color: selected ? "#52B788" : SIDEBAR_SUBTEXT, justifyContent: "center" }}>
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        );
      })}
    </List>
  );
}

interface AppSidebarProps {
  mini: boolean;
  currentPath: string;
  onNavigate: (path: string) => void;
  userName?: string;
  userEmail?: string;
  onLogout?: () => void;
}

export function AppSidebar({
  mini,
  currentPath,
  onNavigate,
  userName,
  userEmail,
  onLogout
}: AppSidebarProps) {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);

  // Easter egg: 5 tap sul logo (entro ~1.2s tra l'uno e l'altro) → dialog Versioni.
  const openVersions = useVersionsStore((s) => s.setOpen);
  const tapCount = useRef(0);
  const tapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onLogoTap = () => {
    tapCount.current += 1;
    if (tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(() => { tapCount.current = 0; }, 1200);
    if (tapCount.current >= 5) {
      tapCount.current = 0;
      openVersions(true);
    }
  };

  const initials = userName
    ? userName.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2)
    : "KA";

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      bgcolor: SIDEBAR_BG,
      overflow: "hidden"
    }}>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: mini ? "center" : "flex-start",
        gap: mini ? 0 : 1.5,
        px: mini ? 0.5 : 2.5,
        py: mini ? 1.5 : 2.5,
        borderBottom: `1px solid ${SIDEBAR_DIVIDER}`,
        minHeight: mini ? 56 : undefined
      }}>
        {mini ? (
          <Tooltip title={t("sidebar.brand")} placement="right">
            <Box onClick={onLogoTap} sx={{ display: "flex", justifyContent: "center", cursor: "pointer" }}>
              <KappaLogo size={28} />
            </Box>
          </Tooltip>
        ) : (
          <>
            <Box onClick={onLogoTap} sx={{ cursor: "pointer", display: "flex" }}>
              <KappaLogo size={32} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: "#FFFFFF", fontWeight: 700, fontSize: "0.95rem", lineHeight: 1.2 }}>
                {t("sidebar.brand")}
              </Typography>
              <Typography sx={{ color: SIDEBAR_SUBTEXT, fontSize: "0.7rem" }}>
                {t("sidebar.brandSubtitle")}
              </Typography>
            </Box>
          </>
        )}
      </Box>

      <Box sx={{
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        pt: 1,
        "&::-webkit-scrollbar": { width: 4 },
        "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(255,255,255,0.15)", borderRadius: 2 }
      }}>
        {mini ? (
          <MiniNavList currentPath={currentPath} onNavigate={onNavigate} />
        ) : (
          NAV_GROUPS.map((group) => (
            <NavGroupBlock
              key={group.labelKey || "root"}
              group={group}
              currentPath={currentPath}
              onNavigate={onNavigate}
            />
          ))
        )}
      </Box>

      <Box sx={{ borderTop: `1px solid ${SIDEBAR_DIVIDER}`, p: mini ? 0.75 : 1.5 }}>
        {mini ? (
          <Tooltip title={`${userName ?? t("sidebar.manager")}${userEmail ? ` — ${userEmail}` : ""}`} placement="right">
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 0.5,
                borderRadius: 2,
                cursor: "pointer",
                "&:hover": { bgcolor: SIDEBAR_HOVER }
              }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: "#2D6A4F", fontSize: "0.8rem" }}>
                {initials}
              </Avatar>
            </Box>
          </Tooltip>
        ) : (
          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex", alignItems: "center", gap: 1.5,
              px: 1.5, py: 1, borderRadius: 2, cursor: "pointer",
              "&:hover": { bgcolor: SIDEBAR_HOVER }
            }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: "#2D6A4F", fontSize: "0.8rem" }}>
              {initials}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ color: SIDEBAR_TEXT, fontSize: "0.8rem", fontWeight: 600 }}>
                {userName ?? t("sidebar.manager")}
              </Typography>
              <Typography sx={{ color: SIDEBAR_SUBTEXT, fontSize: "0.68rem" }} noWrap>
                {userEmail ?? ""}
              </Typography>
            </Box>
          </Box>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <MenuItem onClick={() => { onNavigate("/protected/users"); setAnchorEl(null); }}>
            <AccountCircleIcon fontSize="small" sx={{ mr: 1 }} /> {t("sidebar.profile")}
          </MenuItem>
          <MenuItem onClick={(e) => setLangAnchorEl(e.currentTarget)}>
            <TranslateIcon fontSize="small" sx={{ mr: 1 }} /> {t("common.language")}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => { onLogout?.(); setAnchorEl(null); }} sx={{ color: "error.main" }}>
            {t("nav.logout")}
          </MenuItem>
        </Menu>
        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={() => setLangAnchorEl(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          {SUPPORTED_LANGUAGES.map((lng) => (
            <MenuItem
              key={lng}
              selected={i18n.language === lng}
              onClick={() => { changeLanguage(lng as AppLanguage); setLangAnchorEl(null); setAnchorEl(null); }}
            >
              {i18n.language === lng
                ? <CheckIcon fontSize="small" sx={{ mr: 1 }} />
                : <Box sx={{ width: 20, mr: 1, display: "inline-block" }} />}
              {t(lng === "it" ? "common.italian" : "common.english")}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
}

/** Drawer mobile (overlay, si chiude del tutto quando non aperto) */
export function MobileNavDrawer({
  open,
  onClose,
  currentPath,
  onNavigate,
  userName,
  userEmail,
  onLogout
}: {
  open: boolean;
  onClose: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
  userName?: string;
  userEmail?: string;
  onLogout?: () => void;
}) {
  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          borderRight: "none"
        }
      }}
    >
      <AppSidebar
        mini={false}
        currentPath={currentPath}
        onNavigate={(path) => {
          onNavigate(path);
          onClose();
        }}
        userName={userName}
        userEmail={userEmail}
        onLogout={onLogout}
      />
    </Drawer>
  );
}
